import React from 'react';

import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';

import {ChevronRightIcon} from '@chakra-ui/icons';

export type HierarchyTrailItem = {
  id: string;
  name?: string;
  rol?: string;
};

export interface HierarchyTrailProps {
  items: HierarchyTrailItem[];
  onItemClick?: (id: string) => void;
}

const MAX_INLINE_ANCESTORS = 3;

function ItemTooltipLabel({item}: {item: HierarchyTrailItem}) {
  return (
    <Box>
      <Text fontSize='xs'>{item.name}</Text>
      {item.rol && (
        <Text fontSize='xs' opacity={0.8}>
          {item.rol}
        </Text>
      )}
    </Box>
  );
}

function AncestorLink({
  item,
  onClick,
}: {
  item: HierarchyTrailItem;
  onClick?: (id: string) => void;
}) {
  const interactive = Boolean(onClick);
  return (
    <Tooltip
      label={<ItemTooltipLabel item={item} />}
      placement='top'
      openDelay={300}
      hasArrow>
      <BreadcrumbLink
        as={interactive ? 'button' : 'span'}
        type={interactive ? 'button' : undefined}
        isTruncated
        onClick={interactive ? () => onClick?.(item.id) : undefined}
        cursor={interactive ? 'pointer' : 'default'}
        fontSize='xs'
        fontWeight='normal'
        color='gray.500'
        bg='transparent'
        textAlign='left'
        _focus={{boxShadow: 'none'}}>
        {item.name}
      </BreadcrumbLink>
    </Tooltip>
  );
}

function CollapsedAncestorsMenu({
  items,
  onItemClick,
}: {
  items: HierarchyTrailItem[];
  onItemClick?: (id: string) => void;
}) {
  return (
    <Menu placement='bottom-end' isLazy>
      <MenuButton
        aria-label='Mostrar niveles intermedios'
        px={1}
        py={0}
        minW='auto'
        h='auto'
        bg='transparent'
        fontSize='xs'
        color='gray.500'
        lineHeight={1}
        borderRadius='sm'
        position='relative'
        _hover={{color: 'gray.700', bg: 'transparent'}}
        _active={{bg: 'transparent'}}
        _focusVisible={{boxShadow: 'outline'}}
        _before={{
          content: '""',
          position: 'absolute',
          top: '-6px',
          right: '-4px',
          bottom: '-6px',
          left: '-4px',
        }}>
        …
      </MenuButton>
      <MenuList
        bg='white'
        color='gray.900'
        borderWidth='1px'
        borderColor='gray.100'
        borderRadius='md'
        boxShadow='md'
        zIndex={1400}
        minW='220px'
        py={1}>
        {items.map(item => (
          <MenuItem
            key={item.id}
            onClick={() => onItemClick?.(item.id)}
            bg='white'
            color='gray.900'
            fontWeight='normal'
            justifyContent='flex-start'
            _hover={{bg: 'gray.50'}}
            _focus={{bg: 'gray.50'}}>
            <VStack align='start' spacing={0}>
              <Text fontSize='sm' color='gray.900'>
                {item.name}
              </Text>
              {item.rol && (
                <Text fontSize='xs' color='gray.500'>
                  {item.rol}
                </Text>
              )}
            </VStack>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}

function getCurrentBlockInteractiveStyles(interactive: boolean) {
  if (!interactive) {
    return undefined;
  }

  return {
    '&:hover .hierarchy-current-name': {color: 'gray.700'},
    '&:focus-visible': {
      outline: 'none',
      boxShadow: 'outline',
      borderRadius: 'sm',
    },
  };
}

function getCurrentBlockInteractionProps({
  interactive,
  currentId,
  onItemClick,
}: {
  interactive: boolean;
  currentId: string;
  onItemClick?: (id: string) => void;
}) {
  if (!interactive) {
    return {
      role: undefined,
      tabIndex: -1,
      onClick: undefined,
      onKeyDown: undefined,
      cursor: 'default' as const,
    };
  }

  const activate = () => onItemClick?.(currentId);
  return {
    role: 'button' as const,
    tabIndex: 0,
    onClick: activate,
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      activate();
    },
    cursor: 'pointer' as const,
  };
}

function CurrentBlock({
  current,
  parent,
  onItemClick,
  hasAncestors,
}: {
  current: HierarchyTrailItem;
  parent?: HierarchyTrailItem;
  onItemClick?: (id: string) => void;
  hasAncestors: boolean;
}) {
  const interactive = Boolean(onItemClick);
  const parentName = parent?.name?.trim();
  const interactionProps = getCurrentBlockInteractionProps({
    interactive,
    currentId: current.id,
    onItemClick,
  });

  const block = (
    <Box
      mt={hasAncestors ? 2 : 0}
      aria-current='page'
      {...interactionProps}
      sx={getCurrentBlockInteractiveStyles(interactive)}>
      <Text fontSize='sm' lineHeight='1.3' noOfLines={2}>
        <Text
          as='span'
          className='hierarchy-current-name'
          fontWeight='bold'
          color='gray.900'>
          {current.name}
        </Text>
        {current.rol && (
          <Text as='span' fontSize='xs' color='gray.500' ml={1}>
            {'· '}
            {current.rol}
          </Text>
        )}
      </Text>
    </Box>
  );

  if (!interactive || !parentName) {
    return block;
  }

  return (
    <Tooltip
      label={`Volver a ${parentName}`}
      placement='top'
      openDelay={300}
      hasArrow>
      {block}
    </Tooltip>
  );
}

export function HierarchyTrail({items, onItemClick}: HierarchyTrailProps) {
  if (!items.length) {
    return null;
  }

  const ancestors = items.slice(0, -1);
  const current = items[items.length - 1];
  const useCollapse = ancestors.length > MAX_INLINE_ANCESTORS;
  const parent =
    ancestors.length > 0 ? ancestors[ancestors.length - 1] : undefined;

  const ancestorFocusSx = {
    '&:has(:focus-visible)': {
      boxShadow: 'outline',
      borderRadius: 'sm',
    },
  };

  const ancestorItems: React.ReactNode[] = useCollapse
    ? [
        <BreadcrumbItem
          key={`ancestor-first-${ancestors[0].id}`}
          display='inline-flex'
          alignItems='center'
          overflow='hidden'
          minW={0}
          sx={ancestorFocusSx}>
          <AncestorLink item={ancestors[0]} onClick={onItemClick} />
        </BreadcrumbItem>,
        <BreadcrumbItem
          key='collapsed-ancestors'
          display='inline-flex'
          alignItems='center'
          flexShrink={0}>
          <CollapsedAncestorsMenu
            items={ancestors.slice(1, -1)}
            onItemClick={onItemClick}
          />
        </BreadcrumbItem>,
        <BreadcrumbItem
          key={`ancestor-last-${ancestors[ancestors.length - 1].id}`}
          display='inline-flex'
          alignItems='center'
          overflow='hidden'
          minW={0}
          sx={ancestorFocusSx}>
          <AncestorLink
            item={ancestors[ancestors.length - 1]}
            onClick={onItemClick}
          />
        </BreadcrumbItem>,
      ]
    : ancestors.map(ancestor => (
        <BreadcrumbItem
          key={ancestor.id}
          display='inline-flex'
          alignItems='center'
          overflow='hidden'
          minW={0}
          sx={ancestorFocusSx}>
          <AncestorLink item={ancestor} onClick={onItemClick} />
        </BreadcrumbItem>
      ));

  return (
    <Box textAlign='left'>
      {ancestors.length > 0 && (
        <Breadcrumb
          lineHeight='1.4'
          spacing={1}
          separator={
            <ChevronRightIcon w={2.5} h={2.5} color='gray.300' flexShrink={0} />
          }
          sx={{
            '& ol': {
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              minW: 0,
            },
            '& li': {
              minW: 0,
            },
          }}>
          {ancestorItems}
        </Breadcrumb>
      )}

      <CurrentBlock
        current={current}
        parent={parent}
        onItemClick={onItemClick}
        hasAncestors={ancestors.length > 0}
      />
    </Box>
  );
}
